import { STATUS, STEP_TYPE } from '@/constants/common'
import { TStatus } from '@/types/task.interface'

type TUploadFileItemTimer = {
	minutes: number
	seconds: number
}

export type TUploadFileItemViewKind =
	| 'idle'
	| 'uploading'
	| 'uploaded'
	| 'transcribing'
	| 'finished'
	| 'failed'

export interface IGetUploadFileItemViewStateParams {
	selectedWay: string
	progress: number
	isUploading: boolean
	isTranscribing: boolean
	isUploaded: boolean
	error: boolean
	hasFile: boolean
	localHasVideo: boolean
	draftStatus: TStatus
	draftHasVideo: boolean
	timer: TUploadFileItemTimer
}

export interface IUploadFileItemViewState {
	kind: TUploadFileItemViewKind
	statusText: string
	showSpinner: boolean
	showSuccessIcon: boolean
}

const formatUploadItemTimer = (timer: TUploadFileItemTimer) =>
	`${timer.minutes.toString().padStart(2, '0')}:${timer.seconds
		.toString()
		.padStart(2, '0')}`

export const getUploadFileItemViewState = ({
	selectedWay,
	progress,
	isUploading,
	isTranscribing,
	isUploaded,
	error,
	hasFile,
	localHasVideo,
	draftStatus,
	draftHasVideo,
	timer
}: IGetUploadFileItemViewStateParams): IUploadFileItemViewState => {
	if (error) {
		return {
			kind: 'failed',
			statusText: 'Failed',
			showSpinner: false,
			showSuccessIcon: false
		}
	}

	if (
		selectedWay === STEP_TYPE.TRANSCRIBE &&
		(isTranscribing || draftStatus === STATUS.IN_PROGRESS)
	) {
		return {
			kind: 'transcribing',
			statusText: `Transcribing ${formatUploadItemTimer(timer)}`,
			showSpinner: isUploading && !isUploaded,
			showSuccessIcon: false
		}
	}

	if (draftStatus === STATUS.FINISHED && selectedWay === STEP_TYPE.TRANSCRIBE) {
		return {
			kind: 'finished',
			statusText: 'Finished',
			showSpinner: false,
			showSuccessIcon: true
		}
	}

	if (
		isUploaded ||
		draftHasVideo ||
		(selectedWay === STEP_TYPE.UPLOAD && progress === 100 && !isUploading)
	) {
		return {
			kind: 'uploaded',
			statusText: 'Uploaded',
			showSpinner: false,
			showSuccessIcon: true
		}
	}

	if (hasFile && progress > 0 && !localHasVideo) {
		return {
			kind: 'uploading',
			statusText: `File uploading ${progress}%`,
			showSpinner: isUploading,
			showSuccessIcon: false
		}
	}

	if (!hasFile && draftStatus === STATUS.ADDED) {
		return {
			kind: 'uploading',
			statusText: `File uploading ${formatUploadItemTimer(timer)}`,
			showSpinner: isUploading,
			showSuccessIcon: false
		}
	}

	return {
		kind: 'idle',
		statusText: '',
		showSpinner: isUploading && !isUploaded,
		showSuccessIcon: false
	}
}
